/**
 * `?promo=CODE` on the funnel URL: a Stripe promotion code the backend looks
 * up (GET /funnel/promo) and, when it applies to Pixley Pro, uses as the
 * checkout's discount in place of the funnel's own first-period coupon,
 * on the regular price. Never stacked with the coupon.
 *
 * The code is kept for the funnel session (it has to survive the Google
 * sign-in round trip) and only ever sent to our backend. Only the backend
 * decides whether it is real; the page shows what the backend reports.
 */

export const PROMO_KEY = 'pixley_funnel_promo';
const PROMO_PARAM = 'promo';
const CODE_RE = /^[A-Za-z0-9_-]{2,40}$/;

export const normalizePromoCode = (value) =>
  (typeof value === 'string' && CODE_RE.test(value.trim()) ? value.trim().toUpperCase() : null);

/** Reads ?promo= from the landing URL (a fresh code replaces a stored one)
 *  and returns the code in force for this session, if any. */
export function capturePromoCode() {
  let fromUrl = null;
  try {
    fromUrl = normalizePromoCode(new URLSearchParams(window.location.search).get(PROMO_PARAM));
  } catch (_) {
    // no window / bad URL
  }
  try {
    if (fromUrl) {
      sessionStorage.setItem(PROMO_KEY, fromUrl);
      return fromUrl;
    }
    return normalizePromoCode(sessionStorage.getItem(PROMO_KEY));
  } catch (_) {
    return fromUrl;
  }
}

export const readPromoCode = () => {
  try {
    return normalizePromoCode(sessionStorage.getItem(PROMO_KEY));
  } catch (_) {
    return null;
  }
};

/** The backend's answer, kept only when it says the code applies. */
export const promoFromResponse = (res) => {
  const data = res?.data;
  if (!res?.status || !data || data.valid !== true) return null;
  const cents = data.first_period_cents || {};
  if (!Number.isFinite(cents.monthly) || !Number.isFinite(cents.annual)) return null;
  return {
    code: data.code,
    discountDisplay: data.discount_display || '',
    firstPeriodCents: { monthly: cents.monthly, annual: cents.annual },
  };
};
